import { useEffect, useState } from "react";
import { APP_LOCALE } from "../../config/locale";
import {
  getTreasuryForecast,
  type ForecastItem,
  type TreasuryAlert,
  type TreasuryForecast,
  type TreasuryLevel,
} from "../../tesouraria/tesourariaApi";
import { FinancialKpiCard, formatCurrency } from "../Obras/components/FinancialDashboardShared";

// monthEnd é o último instante do mês (ex.: 30/06 23:59:59.999+00:00); formatar em UTC evita
// que o fuso local (ex.: Europe/Lisbon, UTC+1) faça rolar o rótulo para o dia seguinte.
const longDateFormatter = new Intl.DateTimeFormat(APP_LOCALE, {
  day: "2-digit",
  month: "long",
  year: "numeric",
  timeZone: "UTC",
});

// Data "as of" do saldo de partida — também estável em UTC (mostra o dia escolhido).
const referenceDateFormatter = new Intl.DateTimeFormat(APP_LOCALE, {
  day: "2-digit",
  month: "2-digit",
  year: "numeric",
  timeZone: "UTC",
});

// Datas por evento (alertas) — fuso local; já apresentam o dia correto.
const shortDateFormatter = new Intl.DateTimeFormat(APP_LOCALE, {
  day: "2-digit",
  month: "2-digit",
  year: "numeric",
});

function formatDate(value: string | null, formatter: Intl.DateTimeFormat = longDateFormatter): string {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return formatter.format(date);
}

// Semáforo 🟢🟡🔴 — pares de classes que existem no index.css pré-compilado (ver spec §0).
const LEVEL_META: Record<TreasuryLevel, { badgeClass: string; emoji: string; label: string }> = {
  green: { badgeClass: "bg-green-500/10 text-green-700", emoji: "🟢", label: "Saudável" },
  yellow: { badgeClass: "bg-yellow-500/10 text-yellow-700", emoji: "🟡", label: "Atenção" },
  red: { badgeClass: "bg-red-500/10 text-red-700", emoji: "🔴", label: "Crítico" },
};

const ALERT_META: Record<TreasuryAlert["severity"], { className: string; label: string }> = {
  danger: { className: "bg-red-500/10 text-red-700", label: "Crítico" },
  warning: { className: "bg-yellow-500/10 text-yellow-700", label: "Atenção" },
  info: { className: "bg-primary/10 text-primary", label: "Info" },
};

function sumByCategory(items: ForecastItem[], category: ForecastItem["category"]): number {
  return items
    .filter((item) => item.category === category)
    .reduce((total, item) => total + item.amount, 0);
}

export function TreasuryForecastPanel() {
  const [forecast, setForecast] = useState<TreasuryForecast | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    getTreasuryForecast()
      .then(setForecast)
      .catch((err) =>
        setError(err instanceof Error ? err.message : "Erro ao carregar a previsão de tesouraria."),
      );
  }, []);

  if (error) {
    return <div className="border border-destructive/30 bg-destructive/10 text-destructive px-4 py-3">{error}</div>;
  }
  if (!forecast) {
    return <div className="text-secondary-text py-6">A carregar previsão de tesouraria…</div>;
  }

  const level = LEVEL_META[forecast.level];
  const invoicesTotal = sumByCategory(forecast.inflows, "invoice");
  const milestonesTotal = sumByCategory(forecast.inflows, "milestone");
  const rentTotal = sumByCategory(forecast.inflows, "rent");
  const loansTotal = sumByCategory(forecast.outflows, "loan");
  const expensesTotal = sumByCategory(forecast.outflows, "expense");
  const taxTotal = sumByCategory(forecast.outflows, "tax");

  return (
    <section className="space-y-5">
      <div>
        <h2 className="text-foreground" style={{ fontSize: "var(--text-h3)", fontWeight: "var(--font-weight-semi-bold)" }}>
          Previsão de tesouraria
        </h2>
        <p className="text-secondary-text mt-1">
          Saldo projetado até ao fim do mês ({formatDate(forecast.monthEnd)}), entradas e saídas previstas e alertas.
        </p>
      </div>

      {/* Card destacado — Saldo projetado fim do mês + semáforo */}
      <div className="bg-card border border-border p-5" style={{ borderRadius: "var(--radius-card)" }}>
        <div>
          <div className="text-secondary-text text-sm mb-1">Saldo projetado fim do mês</div>
          <div className="flex items-center flex-wrap gap-3">
            <div
              className="text-foreground"
              style={{ fontSize: "var(--text-h2)", fontWeight: "var(--font-weight-semi-bold)" }}
            >
              {formatCurrency(forecast.projectedBalance)}
            </div>
            <span
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-sm ${level.badgeClass}`}
              style={{ borderRadius: "var(--radius-button)", fontWeight: "var(--font-weight-semi-bold)" }}
            >
              <span aria-hidden="true">{level.emoji}</span>
              {level.label}
            </span>
          </div>
        </div>

        {/* Equação: saldo de partida → +entradas −saídas = projetado */}
        <div className="mt-5 flex flex-wrap items-center gap-x-3 gap-y-2 text-sm">
          <span className="text-secondary-text">
            Saldo de partida{" "}
            <span className="text-foreground" style={{ fontWeight: "var(--font-weight-semi-bold)" }}>
              {formatCurrency(forecast.openingBalance)}
            </span>
            {forecast.openingBalanceAsOf && (
              <span className="text-secondary-text"> (a {formatDate(forecast.openingBalanceAsOf, referenceDateFormatter)})</span>
            )}
          </span>
          <span className="text-secondary-text">+</span>
          <span className="text-secondary-text">
            Entradas{" "}
            <span className="text-green-700" style={{ fontWeight: "var(--font-weight-semi-bold)" }}>
              {formatCurrency(forecast.inflowsTotal)}
            </span>
          </span>
          <span className="text-secondary-text">−</span>
          <span className="text-secondary-text">
            Saídas{" "}
            <span className="text-destructive" style={{ fontWeight: "var(--font-weight-semi-bold)" }}>
              {formatCurrency(forecast.outflowsTotal)}
            </span>
          </span>
          <span className="text-secondary-text">=</span>
          <span className="text-foreground" style={{ fontWeight: "var(--font-weight-semi-bold)" }}>
            {formatCurrency(forecast.projectedBalance)}
          </span>
        </div>
      </div>

      {/* KPI cards Entradas/Saídas previstas até EoM + breakdown por categoria */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <FinancialKpiCard
          label="Entradas previstas até fim do mês"
          value={formatCurrency(forecast.inflowsTotal)}
          hint={`Faturas ${formatCurrency(invoicesTotal)} · Marcos ${formatCurrency(milestonesTotal)} · Rendas ${formatCurrency(rentTotal)}`}
        />
        <FinancialKpiCard
          label="Saídas previstas até fim do mês"
          value={formatCurrency(forecast.outflowsTotal)}
          hint={`Empréstimos ${formatCurrency(loansTotal)} · Despesas ${formatCurrency(expensesTotal)} · Impostos/Obrigações ${formatCurrency(taxTotal)}`}
        />
      </div>

      {/* Alertas — cor por severidade */}
      <div className="bg-card border border-border" style={{ borderRadius: "var(--radius-card)" }}>
        <div className="px-5 py-4 border-b border-border">
          <h3 className="text-foreground" style={{ fontWeight: "var(--font-weight-semi-bold)" }}>
            Alertas
          </h3>
        </div>
        <div className="p-5">
          {forecast.alerts.length === 0 ? (
            <div className="text-secondary-text py-2">Sem alertas de tesouraria.</div>
          ) : (
            <ul className="space-y-2">
              {forecast.alerts.map((alert, index) => {
                const meta = ALERT_META[alert.severity];
                return (
                  <li
                    key={`${alert.severity}-${alert.type}-${index}`}
                    className={`flex items-start justify-between gap-3 px-4 py-3 ${meta.className}`}
                    style={{ borderRadius: "var(--radius-button)" }}
                  >
                    <div className="flex items-start gap-3 min-w-0">
                      <span
                        className="inline-flex shrink-0 px-2 py-0.5 text-xs"
                        style={{ borderRadius: "var(--radius-button)", fontWeight: "var(--font-weight-semi-bold)" }}
                      >
                        {meta.label}
                      </span>
                      <span className="min-w-0">{alert.message}</span>
                    </div>
                    {alert.amount !== null && (
                      <div className="shrink-0 text-right">
                        <div style={{ fontWeight: "var(--font-weight-semi-bold)" }}>{formatCurrency(alert.amount)}</div>
                        {alert.date && (
                          <div className="text-xs opacity-80">{formatDate(alert.date, shortDateFormatter)}</div>
                        )}
                      </div>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </div>
    </section>
  );
}
